import { lazy, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Row } from 'react-table';

import { AddButton } from 'app/components/Button/AddButton';
import FilterDefault from 'app/components/Filter/FilterDefault';
import TableComponent from 'app/components/Table/TableComponent';
import { DEFAULT_TABLE_QUERY, QUERIES } from 'app/components/Table/core/constants';
import { ID, TYPE_MODAL } from 'app/components/Table/core/types';
import RootContentPage from 'app/components/layout/RootContentPage';

import { useAuth } from 'app/modules/auth';
import ItemFilter from 'app/modules/inventory/items/components/ItemFilter';
import {
  checkParamSynced,
  useGetQueryParams,
  useSyncQueryToState,
} from 'app/modules/query-state/hooks';

import { useNavigateWithSendPath } from 'app/helpers/utils';
import { usePolicyInUser } from 'app/hooks/policy';
import { isNil } from 'lodash';

import { PATH } from 'app/constants/path';

import { ReactComponent as IconSync } from 'app/assets/icons/icon-sync-white.svg';

import { itemsColumns } from './core/columns';
import { deleteItem, getItems, syncItemsFromSmartWms } from './core/requests';
import { Item, ItemFilterParams, SmartWmsSyncSummary } from './core/type';
import { FormChangeStatusItem } from './modal/FormChangeStatusItem';
import { InfoCard } from './modal/InfoCard';
import { SuspensedView } from 'app/routing/PrivateRoutes';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const UnableRemoveItem = lazy(() => import('./modal/UnableRemoveItem'));

const ItemManagementPage = () => {
  useSyncQueryToState(window.location.pathname, DEFAULT_TABLE_QUERY);

  const { currentUser, setCurrentUser } = useAuth();
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const isSystemAdminCurrent = permission.systemAdmin;
  const organization = useOrganizationStore((state) => state.state.organization);
  const [isShowFilter, setIsShowFilter] = useState(false);
  const [isShowUnableRemoveItem, setIsShowUnableRemoveItem] = useState(false);
  const [item, setItem] = useState<Item | null>(null);

  const navigate = useNavigateWithSendPath();
  const queryClient = useQueryClient();

  const { synced, curParams } = useGetQueryParams<ItemFilterParams>();
  const isSynced = checkParamSynced(synced);

  const renderFormConfirmation = (originalData: Item, _typeModal: TYPE_MODAL) => {
    if (_typeModal === TYPE_MODAL.INFO) {
      return (
        <InfoCard content="To delete this item, it needs to not exist in any stock run (except for completed stock run)." />
      );
    }
    if (_typeModal === TYPE_MODAL.UNABLE_CHANGE_ACTIVE_STATUS) {
      return (
        <InfoCard content="To inactivate this item, it has to be removed from all the bins." />
      );
    }

    return <FormChangeStatusItem originalData={originalData} nameQuery={QUERIES.ITEMS_LIST} />;
  };
  const onRedirectPage = () => {
    navigate(PATH.ITEMS_ADD);
  };
  const onClickBodyTable = (row: Row) => () => {
    const newItem = row.original as Item;
    if (newItem.id) {
      navigate(PATH.ITEMS_DETAIL.replace(':id', String(newItem.id)));
    }
  };

  const hasActiveFilters = Object.keys(curParams).some(
    (key) => ['statuses'].includes(key) && !isNil(curParams[key]),
  );

  const handleClickFilter = (type: 'on' | 'off') => {
    setIsShowFilter(type === 'on');
  };

  const handleOpenUnableRemoveItem = (el: Item) => () => {
    setItem(el);
    setIsShowUnableRemoveItem(true);
  };

  const handleCloseUnableRemoveItem = () => {
    setIsShowUnableRemoveItem(false);
    setItem(null);
  };

  const { mutate: syncSmartWmsMutation, isLoading: isSyncingSmartWms } = useMutation({
    mutationFn: syncItemsFromSmartWms,
    onSuccess: (summary: SmartWmsSyncSummary) => {
      queryClient.invalidateQueries(QUERIES.ITEMS_LIST);
      const failedSuffix = summary.failed ? `, ${summary.failed} failed` : '';
      toast.success(
        `Synced ${summary.total} items from SmartWMS (${summary.created} created, ${summary.updated} updated${failedSuffix})`,
      );
    },
  });

  const handleSyncFromSmartWms = () => {
    syncSmartWmsMutation();
  };

  return (
    <RootContentPage
      title="Items"
      rootBody={{
        padding: 0,
        border: 'none',
      }}
    >
      <TableComponent
        getList={getItems}
        nameQuery={QUERIES.ITEMS_LIST}
        listHeaderPosition="left"
        originColumns={itemsColumns(handleOpenUnableRemoveItem)}
        onRedirectPage={onRedirectPage}
        titleModal="Item"
        ModalFormConfirmation={renderFormConfirmation}
        onClickBodyTable={onClickBodyTable}
        nameBtnAdd="Add item"
        moreQuery={isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''}
        deleteById={deleteItem}
        contentModal="Deleting this item will also remove it from the assigned bins."
        placeholderSearch="Search with item name, SKU"
        styleModalForm={{
          paddingRight: 0,
        }}
        isBorder={false}
        syncParamUrl
        customListHeader={
          <>
            <AddButton
              title="Sync from SmartWMS"
              onClick={handleSyncFromSmartWms}
              loading={isSyncingSmartWms}
              disabled={isSyncingSmartWms}
              style={{ margin: '0 12px' }}
              color="primary"
              icon={<IconSync style={{ marginRight: 10 }} />}
            />
            <FilterDefault
              visible={isShowFilter}
              onClickFilter={handleClickFilter}
              isFilter={hasActiveFilters}
              content={
                isShowFilter && (
                  <ItemFilter openFilter={isShowFilter} setIsOpenFilter={setIsShowFilter} />
                )
              }
            />
          </>
        }
      />
      <SuspensedView>
        {isShowUnableRemoveItem && item && (
          <UnableRemoveItem
            item={item}
            isOpen={isShowUnableRemoveItem}
            onClose={handleCloseUnableRemoveItem}
          />
        )}
      </SuspensedView>
    </RootContentPage>
  );
};

export default ItemManagementPage;
